'use client';

import React, { Suspense, useState, useEffect, useCallback, useMemo } from 'react';
import { useScrollEdgeFade } from '@/hooks/useScrollEdgeFade';
import { useSearchParams, useRouter } from 'next/navigation';
import { AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFirestoreCrypto, resolveLensTokens } from '@/hooks/useFirestoreCrypto';
import { useFirestoreCollectionStats } from '@/hooks/useFirestoreCollectionStats';
import { useMarketAnalysis, getTokenInsight, isDataStale } from '@/hooks/useMarketAnalysis';
import type { TokenInsight } from '@/hooks/useMarketAnalysis';
import { useCollectionAnalysis, getCollectionInsight } from '@/hooks/useCollectionAnalysis';
import type { CollectionInsight } from '@/hooks/useCollectionAnalysis';
import { getCollectionBlockchain, getCollectionDisplayName } from '@/utils/blockchain';
import { selectableChainIds, isChainId, getChain } from '@/lib/chains';
import Link from 'next/link';
import UnifiedAssetDirectory, {
  UnifiedAsset,
  AssetDeskRead,
  AssetStoryTick,
} from '@/components/sections/UnifiedAssetDirectory';
import MarketHubAdSpot from '@/components/ui/MarketHubAdSpot';
import MarketStatsBoxes from '@/components/market/MarketStatsBoxes';
import { MarketBandRule, whyLine, fmtTokenPrice } from '@/components/market/market-shared';
import MarketTempStrip from '@/components/market/MarketTempStrip';
import MarketMovers, { MoverTileModel } from '@/components/market/MarketMovers';
import MarketDeskNotes from '@/components/market/MarketDeskNotes';
import type { MarketDeskContext, MarketWireItem } from '@/components/market/market-desk-context';
import TokenShareModal from '@/components/market/TokenShareModal';
import NFTShareModal from '@/components/market/NFTShareModal';
import AssetDetailPanel from '@/components/market/AssetDetailPanel';
import ChainTvlChart from '@/components/defi/ChainTvlChart';
import { useDefiLlamaData } from '@/hooks/useDefiLlamaData';

// ── desk-read chips (carried into the tape's inline ◆ expansion) ───────────────────
function chipsForToken(insight: TokenInsight): string[] {
  const chips: string[] = [];
  if (insight.signals?.volatility) chips.push(`Volatility ${insight.signals.volatility}`);
  if (insight.globalContext?.btcCorrelation) chips.push(`vs BTC ${insight.globalContext.btcCorrelation.toLowerCase()}`);
  if (insight.ecosystemComparison?.tokenVsEcosystem) chips.push(`${insight.ecosystemComparison.tokenVsEcosystem} chain`);
  return chips;
}

function chipsForCollection(insight: CollectionInsight): string[] {
  const chips: string[] = [];
  if (insight.metrics?.volumeRank) chips.push(`Vol rank #${insight.metrics.volumeRank}`);
  if (insight.metrics?.volumeShare) chips.push(`Vol share ${Math.round(insight.metrics.volumeShare)}%`);
  if (insight.metrics?.floorRank) chips.push(`Floor rank #${insight.metrics.floorRank}`);
  return chips;
}

/**
 * Hook to detect if we're on mobile (client-side only to avoid hydration issues)
 */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return { isMobile, hasMounted };
}

interface MarketTerminalProps {
  /** Chain lens — replaces the `useSearchParams().get('chain')` read. */
  chainId?: string;
  /** true = rendered inside the dashboard shell (no standalone chrome). */
  embedded?: boolean;
  /**
   * Server-fetched record context (latest brief mentions + fresh wire items) —
   * powers the tape's story-ticks. Plain JSON across the RSC boundary; absent
   * (standalone /market) → no ticks, zero behavior change.
   */
  deskContext?: MarketDeskContext;
}

export default function MarketTerminal({ chainId, embedded = false, deskContext }: MarketTerminalProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isMobile, hasMounted } = useIsMobile();

  // Chain lens — `?chain=<id>` filters the whole market to one chain (the per-chain
  // "All <chain> markets" target). Omitted = the cross-chain "All chains" aggregate.
  // chainId now comes from the PROP (not searchParams); preserve the existing
  // isChainId validation semantics.
  const chainMeta = chainId ? getChain(chainId) : undefined;
  const chainIdValidated = chainId && isChainId(chainId) ? chainId : undefined;

  const [activeTab, setActiveTab] = useState<'tokens' | 'nfts'>('tokens');
  const [selectedAsset, setSelectedAsset] = useState<UnifiedAsset | null>(null);
  const [showTokenShareModal, setShowTokenShareModal] = useState(false);
  const [showNFTShareModal, setShowNFTShareModal] = useState(false);

  const {
    data: cryptoData,
    loading: tokensLoading,
    error: tokensError
  } = useFirestoreCrypto();

  const { allStats } = useFirestoreCollectionStats({
    enableRealTimeUpdates: true,
  });

  // Get market analysis data for sharing and Abstract F&G
  const { pulse: rawMarketPulse, insights: tokenInsights, loading: pulseLoading } = useMarketAnalysis();
  const { pulse: rawCollectionPulse, insights: collectionInsights } = useCollectionAnalysis();

  // ── the chain-lens data seam ───────────────────────────────────────────────────
  // Two feeds behind this terminal were BOTH gated on one hardcoded `=== 'abstract'`
  // check. They are different problems and now get different flags — collapsing them
  // was what made the token feed permanently Abstract-only.
  //
  // 1. THE TOKEN FEED is genuinely multi-chain in the data: `crypto_prices` carries
  //    `tokensByChain[<chainId>]` next to the legacy flat `abstract_tokens`. So the honest
  //    question is not "is this Abstract?" but "does this chain actually have priced
  //    tokens?" — derived from the data, mirroring the server's resolveChainTokenArray
  //    (src/lib/hub/chain-hub-data.ts). This is deliberately NOT derived from the chain
  //    registry's `feeds.tokens`: EVERY chain declares a DexScreener slug (including the
  //    NFT-only ones), so a config-derived flag would flip a chain on before it had a
  //    single priced token and re-create the exact bug this seam exists to stop — an empty
  //    table under the lens's name. Data-derived means it self-heals the moment a chain's
  //    tokens land in `tracked_tokens`, with no code change.
  // 2. THE MARKET-ANALYSIS PULSE (`market_analysis/pulse` — Fear & Greed, breadth, movers)
  //    is a genuinely UN-KEYED, single global doc computed off Abstract. There is no
  //    per-chain version to show, so it stays Abstract-only until that doc is chain-keyed.
  //    Rendering it under another lens is the "/avax/market showed Abstract's F&G" bug.
  // 3. THE COLLECTION-ANALYSIS PULSE (`collection_analysis/pulse` — the desk's overall NFT
  //    read, ZONE 4's "NFTs" note) is the SAME class of problem as (2), found the same way:
  //    /avax/market's ◆ Desk notes rail was serving an Abstract NFT read (OCH Genesis Hero)
  //    under the Avalanche lens (2026-08-14). One global doc, no chain dimension anywhere in
  //    it — `CollectionPulse` carries summary/timestamp/metrics only, and its metrics are
  //    aggregated across every tracked collection (35 abstract / 10 ethereum / 2 ronin), so
  //    there is nothing to FILTER on; the honest move is to withhold it, not to re-attribute
  //    it. Under a non-abstract lens both pulses go null and `MarketDeskNotes` falls to its
  //    own quiet state (nothing fresh ⇒ no panel, never an empty box).
  //
  //    It gets its OWN flag rather than sharing (2)'s. These are two different docs on two
  //    different backfill clocks, and collapsing distinct feeds behind one `=== 'abstract'`
  //    check is the exact mistake this seam was carved to undo — a shared flag means whichever
  //    doc gets chain-keyed first cannot be switched on without dragging the other along.
  const lensTokens = useMemo(
    () => resolveLensTokens(cryptoData, chainIdValidated),
    [cryptoData, chainIdValidated],
  );

  // `lensTokens` REPLACES the old `lensHasTokenFeed` boolean outright: the array IS the flag
  // (empty ⇒ this lens has no token feed), so there is no second derived value that can drift
  // out of step with the data it claims to describe.
  const lensHasMarketPulse = !chainIdValidated || chainIdValidated === 'abstract';
  const marketPulse = lensHasMarketPulse ? rawMarketPulse : null;

  const lensHasCollectionPulse = !chainIdValidated || chainIdValidated === 'abstract';
  const collectionPulse = lensHasCollectionPulse ? rawCollectionPulse : null;

  // Get DeFiLlama data for TVL chart + the embedded lead band's KPI rail.
  // Chain-scoped: `defi_ecosystem/<chainId>` is written per chain by DefiLlamaScheduler
  // (it loops selectableChainIds()); calling this bare defaulted to the doc id 'current',
  // which is the ABSTRACT mirror — so every hub showed Abstract's TVL.
  const { data: defiData, loading: defiLoading } = useDefiLlamaData(chainIdValidated);

  // Calculate counts. Both respect the chain lens: tokens come from the lens's own token
  // array (empty for a chain with no priced tokens), NFT collections are filtered by their
  // `blockchain` config.
  const tokenCount = lensTokens.filter(
    t => !['BTC', 'ETH', 'BITCOIN', 'ETHEREUM'].includes(t.symbol.toUpperCase())
  ).length;
  const nftCount = Object.values(allStats || {}).filter(
    s => !chainIdValidated || getCollectionBlockchain(s.collection) === chainIdValidated
  ).length;

  // Get market movers from the LENS's token array: these tiles fed topGainer/topLoser/top5*
  // straight from the Abstract-only feed with no lens check, so /avax/market listed Abstract
  // symbols as Avalanche movers. A chain with no priced tokens yields [] and renders absent.
  const abstractTokens = lensTokens.filter(
    t => !['BTC', 'ETH', 'BITCOIN', 'ETHEREUM', 'PENGU'].includes(t.symbol.toUpperCase())
  );

  const topGainer = abstractTokens.reduce((max, token) =>
    (token.priceChange24h || 0) > (max?.priceChange24h || -Infinity) ? token : max, abstractTokens[0]);
  const topLoser = abstractTokens.reduce((min, token) =>
    (token.priceChange24h || 0) < (min?.priceChange24h || Infinity) ? token : min, abstractTokens[0]);

  const tokenGainers = abstractTokens.filter(t => (t.priceChange24h || 0) > 0).length;
  const tokenLosers = abstractTokens.filter(t => (t.priceChange24h || 0) < 0).length;

  // Calculate top 5 gainers and losers for sharing (include images from DexScreener)
  const top5Gainers = [...abstractTokens]
    .filter(t => (t.priceChange24h || 0) > 0)
    .sort((a, b) => (b.priceChange24h || 0) - (a.priceChange24h || 0))
    .slice(0, 5)
    .map(t => ({
      symbol: t.symbol,
      change: t.priceChange24h || 0,
      imageUrl: t.customImage || t.imageUrl || null,
    }));

  const top5Losers = [...abstractTokens]
    .filter(t => (t.priceChange24h || 0) < 0)
    .sort((a, b) => (a.priceChange24h || 0) - (b.priceChange24h || 0))
    .slice(0, 5)
    .map(t => ({
      symbol: t.symbol,
      change: t.priceChange24h || 0,
      imageUrl: t.customImage || t.imageUrl || null,
    }));

  // Multi-chain (B1.5): surface every in-scope chain, not just Abstract.
  // "In-scope" = the chains we actually surface today (selectableChainIds():
  // abstract/base/ethereum). Base/Ethereum collections were previously dropped here.
  // Membership is "includes" — never a hardcoded `=== 'abstract'`.
  // Uses the blockchain config from shared/config/monitored-collections.js.
  const inScopeChains = selectableChainIds();
  const inScopeCollections = Object.values(allStats || {}).filter(stat => {
    const blockchain = getCollectionBlockchain(stat.collection);
    return inScopeChains.includes(blockchain);
  });

  // Calculate NFT highlights for sharing (top 3 by 24H volume - ONLY use oneDayVolume!)
  // IMPORTANT: Never fall back to totalVolume as that's ALL TIME, not 24H
  const nftHighlights = inScopeCollections
    .filter(stat => (stat.oneDayVolume || 0) > 0) // Only include collections with 24H volume
    .sort((a, b) => (b.oneDayVolume || 0) - (a.oneDayVolume || 0))
    .slice(0, 3)
    .map(stat => ({
      name: stat.collection,
      displayName: getCollectionDisplayName(stat.collection), // Use proper display name
      // floorPrice widened to number|null (WS-K); NFTHighlight expects number and
      // these highlight cards treat absent-floor as 0 downstream — coalesce here.
      floorPrice: stat.floorPrice ?? 0,
      floorSymbol: stat.floorPriceSymbol || 'ETH',
      oneDayChange: stat.oneDayChange,
      volume24h: stat.oneDayVolume || 0, // ONLY 24H volume, never totalVolume
      imageUrl: stat.imageUrl || null, // Include collection image
    }));

  // Calculate total 24H NFT volume (in-scope chains: abstract/base/ethereum)
  const totalNFTVolume24h = inScopeCollections.reduce(
    (sum, stat) => sum + (stat.oneDayVolume || 0),
    0
  );

  // Count in-scope-chain collections (abstract/base/ethereum)
  const inScopeNftCount = inScopeCollections.length;

  // Lens-scoped top NFT by 24h volume (for the embedded "Desk reads" band) — when a
  // chain lens is active, the pick must respect it (inScopeCollections is cross-chain).
  const lensCollections = chainIdValidated
    ? Object.values(allStats || {}).filter(
        s => getCollectionBlockchain(s.collection) === chainIdValidated
      )
    : inScopeCollections;
  const lensTopNft = lensCollections
    .filter(stat => (stat.oneDayVolume || 0) > 0)
    .sort((a, b) => (b.oneDayVolume || 0) - (a.oneDayVolume || 0))[0];

  // ── desk intel for the tape (market-v2): per-asset reads + story-ticks ──────────
  // deskReads: every asset with a produced insight doc, keyed by UnifiedAsset.id —
  //   `fresh` (<12h) gates the ◆ inline expansion; entitySlug doors into the dossier.
  // storyTicks: the asset's entity appeared on a fresh wire item (matched via the
  //   insight's socialContext.handleId == breaking sourceHandleId) or in the latest
  //   brief's mentionedEntities ($SYMBOL / @handle / slug). Wire wins (fresher door).
  const deskIntel = useMemo(() => {
    if (!embedded) return { deskReads: undefined, storyTicks: undefined };
    const deskReads: Record<string, AssetDeskRead> = {};
    const storyTicks: Record<string, AssetStoryTick> = {};
    const recap = deskContext?.recap;
    const mentions = new Set(recap?.mentions ?? []);
    const wireByHandle = new Map<string, MarketWireItem>();
    for (const w of deskContext?.wire ?? []) {
      if (w.handleId && !wireByHandle.has(w.handleId)) wireByHandle.set(w.handleId, w);
    }

    const applyTick = (id: string, handle: string | undefined, tokens: Array<string | undefined>) => {
      const wireHit = handle ? wireByHandle.get(handle) : undefined;
      if (wireHit) {
        storyTicks[id] = { kind: 'wire', label: `On the wire — ${wireHit.title}`, href: `/breaking/${wireHit.slug}` };
        return;
      }
      if (recap && [...tokens, handle].some((t) => t && mentions.has(t))) {
        storyTicks[id] = { kind: 'brief', label: `In the desk's latest brief — ${recap.title}`, href: recap.href };
      }
    };

    // Desk reads describe the tokens THIS lens shows — iterating the raw Abstract feed here
    // built Abstract desk reads under every lens.
    for (const t of lensTokens) {
      if (['BTC', 'ETH', 'BITCOIN', 'ETHEREUM'].includes(t.symbol.toUpperCase())) continue;
      const id = `token-${t.symbol}`;
      const insight = getTokenInsight(tokenInsights, t.symbol);
      const handle = insight?.socialContext?.handleId?.toLowerCase();
      const entitySlug = insight?.socialContext?.entitySlug || undefined;
      applyTick(id, handle, [t.symbol.toLowerCase(), entitySlug?.toLowerCase()]);
      if (insight?.analysis) {
        deskReads[id] = {
          analysis: insight.analysis,
          timestamp: insight.timestamp,
          fresh: !isDataStale(insight.timestamp, 12),
          chips: chipsForToken(insight),
          entitySlug,
          entityName: insight.socialContext?.entityName || undefined,
        };
      }
    }

    for (const slug of Object.keys(allStats || {})) {
      const id = `nft-${slug}`;
      const insight = getCollectionInsight(collectionInsights, slug);
      const handle = insight?.socialContext?.handleId?.toLowerCase();
      const entitySlug = insight?.socialContext?.entitySlug || undefined;
      applyTick(id, handle, [slug.toLowerCase(), entitySlug?.toLowerCase()]);
      if (insight?.analysis) {
        deskReads[id] = {
          analysis: insight.analysis,
          timestamp: insight.timestamp,
          fresh: !isDataStale(insight.timestamp, 12),
          chips: chipsForCollection(insight),
          entitySlug,
          entityName: insight.socialContext?.entityName || undefined,
        };
      }
    }

    return { deskReads, storyTicks };
  }, [embedded, deskContext, lensTokens, allStats, tokenInsights, collectionInsights]);

  // ── mover tiles (market-v2 hero) — the pulse's own picks + the lens top NFT ─────
  // A tile renders with a why-line ONLY when a fresh (<12h) produced read exists;
  // otherwise it renders number-only (smaller). No honest number at all → no tile.
  const moverTiles = useMemo<MoverTileModel[]>(() => {
    if (!embedded) return [];
    const tiles: MoverTileModel[] = [];
    const seen = new Set<string>();
    const picks = [
      { symbol: marketPulse?.metrics?.topGainer?.symbol, role: 'Top gainer' },
      { symbol: marketPulse?.metrics?.topLoser?.symbol, role: 'Top loser' },
      { symbol: marketPulse?.metrics?.highestVolume?.symbol, role: 'Vol leader' },
    ];
    for (const pick of picks) {
      if (!pick.symbol) continue;
      const key = pick.symbol.toUpperCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const token = abstractTokens.find((t) => t.symbol.toUpperCase() === key);
      const insight = getTokenInsight(tokenInsights, pick.symbol);
      const fresh = insight && !isDataStale(insight.timestamp, 12) ? insight : null;
      const price = token?.currentPrice ?? fresh?.metrics?.price;
      if (price == null || price <= 0) continue;
      tiles.push({
        key: `tkn-${key}`,
        symbol: key,
        role: pick.role,
        priceLabel: fmtTokenPrice(price),
        change24h: token?.priceChange24h ?? fresh?.metrics?.change24h,
        why: fresh ? whyLine(fresh.analysis) : undefined,
        whyTimestamp: fresh?.timestamp,
        entitySlug: fresh?.socialContext?.entitySlug || undefined,
        sparkSymbol: key,
      });
    }
    if (lensTopNft) {
      const insight = getCollectionInsight(collectionInsights, lensTopNft.collection);
      const fresh = insight && !isDataStale(insight.timestamp, 12) ? insight : null;
      const floor = lensTopNft.floorPrice || 0;
      const sym = lensTopNft.floorPriceSymbol || 'ETH';
      if (floor > 0 || (lensTopNft.oneDayVolume || 0) > 0) {
        tiles.push({
          key: `nft-${lensTopNft.collection}`,
          symbol: getCollectionDisplayName(lensTopNft.collection).toUpperCase(),
          role: 'NFT · Vol leader',
          priceLabel: floor > 0 ? (floor >= 1 ? floor.toFixed(2) : floor >= 0.01 ? floor.toFixed(3) : floor.toFixed(4)) : '—',
          unit: `${sym} floor`,
          change24h: lensTopNft.oneDayChange || undefined,
          sub: (lensTopNft.oneDayVolume || 0) > 0 ? `${(lensTopNft.oneDayVolume || 0).toFixed(2)} ${sym} vol 24h` : undefined,
          why: fresh ? whyLine(fresh.analysis) : undefined,
          whyTimestamp: fresh?.timestamp,
          entitySlug: fresh?.socialContext?.entitySlug || undefined,
        });
      }
    }
    return tiles;
  }, [embedded, marketPulse, abstractTokens, tokenInsights, collectionInsights, lensTopNft]);

  // Get AI insights for top movers (for share card)
  const topGainerSymbol = top5Gainers[0]?.symbol;
  const topGainerInsight = topGainerSymbol ? getTokenInsight(tokenInsights, topGainerSymbol) : null;

  const topNFTSlug = nftHighlights[0]?.name;
  const topNFTInsight = topNFTSlug ? getCollectionInsight(collectionInsights, topNFTSlug) : null;

  // Handle asset selection with URL update
  const handleAssetSelect = useCallback((asset: UnifiedAsset | null) => {
    setSelectedAsset(asset);

    // When embedded in the dashboard shell, keep selection in state ONLY — a
    // router.replace would write a wrong `/market` path while the user is on
    // `/[chain]/market`. Standalone preserves today's `?asset=` URL-sync verbatim.
    if (embedded) {
      return;
    }

    // Update URL with selected asset
    const params = new URLSearchParams(searchParams.toString());
    if (asset) {
      const assetParam = asset.type === 'token' ? asset.symbol?.toLowerCase() : asset.slug;
      if (assetParam) {
        params.set('asset', assetParam);
      }
    } else {
      params.delete('asset');
    }
    const newUrl = params.toString() ? `/market?${params.toString()}` : '/market';
    router.replace(newUrl, { scroll: false });
  }, [router, searchParams, embedded]);

  // Don't auto-select - let users see market overview first
  const handleFirstAssetReady = useCallback((_asset: UnifiedAsset) => {
    // Intentionally not auto-selecting to show market overview
  }, []);

  const handleClosePanel = useCallback(() => {
    handleAssetSelect(null);
  }, [handleAssetSelect]);

  // ── sticky-rail scroll fades ──────────────────────────────────────────────────
  // The right rail is a bounded scrollport (sticky + max-h + overflow-y-auto) with
  // its scrollbar hidden, so an overflowing docked card used to hard-slice at the
  // rail's edges. The shared hook feathers any edge content continues past; edges
  // with nothing beyond them stay crisp (no fade when the rail fits).
  const rail = useScrollEdgeFade<HTMLDivElement>([hasMounted, selectedAsset]);

  // Handle share click based on active tab
  const handleShareClick = useCallback(() => {
    if (activeTab === 'tokens') {
      setShowTokenShareModal(true);
    } else {
      setShowNFTShareModal(true);
    }
  }, [activeTab]);

  // Terminal body — reads-first lead band (embedded) / legacy stats (standalone),
  // then the master-detail tape + mobile sheet.
  const body = (
    <>
      {/* Embedded (chain page): THE LAW — ASSETS FIRST, judgment woven into the
          numbers. Zone 1 = the temperature strip (one slim mono row: ONE F&G gauge
          with the global tick, TVL/DEX/breadth/stables/fees). Zone 2 = the hero
          movers with the desk's why-lines (number-first tiles, one clipped line
          from the produced record each). Both self-gate on real data and render
          deterministic skeletons during load (SSR-safe). */}
      {embedded ? (
        <>
          <MarketTempStrip
            chainLabel={chainMeta?.label ?? 'Abstract'}
            marketPulse={marketPulse}
            pulseLoading={pulseLoading}
            defiData={defiData}
            defiLoading={defiLoading}
          />
          <MarketMovers
            tiles={moverTiles}
            loading={(pulseLoading || tokensLoading) && moverTiles.length === 0}
          />
        </>
      ) : (
        /* Standalone /market keeps today's terminal header verbatim. */
        hasMounted && (
          <MarketStatsBoxes
            bare={false}
            tokenCount={tokenCount}
            nftCount={nftCount}
            topGainer={topGainer}
            topLoser={topLoser}
            tokenGainers={tokenGainers}
            tokenLosers={tokenLosers}
            activeTab={activeTab}
          />
        )
      )}

      {/* Error State */}
      {tokensError && (
        <div className="bg-rose-100 dark:bg-rose-900/20 border-b border-rose-300 dark:border-rose-800">
          {/* Embedded: inherit the dashboard shell's content container (no own max-w/px). */}
          <div className={embedded ? 'py-2' : 'max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-2'}>
            <div className="flex items-center gap-2">
              <AlertCircle className="text-rose-600 dark:text-rose-400" size={14} />
              <span className="font-mono text-xs text-rose-700 dark:text-rose-300">{tokensError}</span>
            </div>
          </div>
        </div>
      )}

      {/* Main Content: Master-Detail Layout. Embedded inherits the dashboard shell's
          ink background (no off-token neutral panel); standalone keeps its own bg. */}
      <div className={embedded ? 'flex-1' : 'bg-neutral-50 dark:bg-neutral-950 flex-1'}>
        {/* Embedded: inherit the dashboard shell's content container (no own max-w/px). */}
        <div className={embedded ? 'py-4' : 'max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4'}>
          {/* Chain lens chip — standalone /market only (disambiguates the cross-chain
              view). Suppressed when embedded: the user is unambiguously on /[chain]. */}
          {!embedded && chainMeta && (
            <div className="mb-4 flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                Lens
              </span>
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-xs font-bold text-white"
                style={{ backgroundColor: chainMeta.color }}
              >
                {chainMeta.label}
              </span>
              {/* TODO(multichain): chain-scope this link. `/market` is retired; this
                  block is standalone-only (always suppressed when embedded, which is
                  now the sole render path) — repointed to the in-shell twin to avoid a
                  dead link if the standalone mode is ever revived. */}
              <Link
                href="/abstract/market"
                className="font-mono text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white underline-offset-2 hover:underline"
              >
                View all chains
              </Link>
            </div>
          )}

          {/* Ad Banner */}
          {!embedded && (
            <MarketHubAdSpot variant="banner" showCloseButton className="mb-4" />
          )}

          {/* Embedded: zone rule for the tape (the assets, with ◆ desk affordances). */}
          {embedded && (
            <div className="mb-3">
              <MarketBandRule
                label="◆ The tape"
                right={
                  <>
                    {tokenCount} tokens · {nftCount} NFTs · <span className="text-amber-600 dark:text-scout-gold">◆</span> fresh desk read
                  </>
                }
              />
            </div>
          )}

          {/* Embedded mobile (390): the TVL chart slots between movers and the tape
              (spec order strip → movers → chart → table → notes). Desktop keeps it
              pinned at the right rail's top. */}
          {embedded && hasMounted && !selectedAsset && defiData?.historicalTvl && defiData.historicalTvl.length > 0 && (
            <div className="lg:hidden mb-4">
              <ChainTvlChart
                data={defiData.historicalTvl}
                currentTvl={defiData.tvl}
                tvlChange24h={defiData.tvlChange24h}
                tvlChange7d={defiData.tvlChange7d}
                chainId={chainIdValidated}
              />
            </div>
          )}

          {/* Master-Detail Grid - Side by side on desktop. Embedded: the TAPE sits
              LEFT (immediately after the movers in reading order) and the rail
              (TVL chart + desk notes / docked detail) RIGHT — via lg:order swap so
              the standalone layout stays byte-identical. */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Asset Detail Panel / right rail (Sticky Sidebar) */}
            {/* Only render after mount to avoid hydration mismatch from Firebase data */}
            {/* Embedded sticky offset/bound mirror the social rail convention: 68px =
                56px TopBar + 12px breathing room; 84px = that offset + a 16px bottom
                gap. Standalone keeps its navbar-tuned offsets verbatim. */}
            <div
              ref={rail.ref}
              onScroll={rail.onScroll}
              className={`hidden lg:block lg:col-span-4 lg:sticky lg:overflow-y-auto lg:overscroll-contain scrollbar-hide ${
                embedded
                  ? 'lg:top-[68px] lg:max-h-[calc(100vh-84px)] lg:order-2'
                  : 'lg:top-24 lg:max-h-[calc(100vh-7rem)]'
              }`}
            >
              {hasMounted ? (
                <div className="space-y-4">
                  {/* TVL Chart - Show when no asset selected. Chain-aware end to end: the
                      DATA came from `useDefiLlamaData(chainIdValidated)` above, and the
                      BRANDING (label/logo/DeFiLlama link) now comes from the same chain id
                      via the registry — the two used to disagree, so /avax/market showed
                      Avalanche's real TVL under Abstract's name (closes the B1.x TODO). */}
                  {!selectedAsset && defiData?.historicalTvl && defiData.historicalTvl.length > 0 && (
                    <ChainTvlChart
                      data={defiData.historicalTvl}
                      currentTvl={defiData.tvl}
                      tvlChange24h={defiData.tvlChange24h}
                      tvlChange7d={defiData.tvlChange7d}
                      chainId={chainIdValidated}
                    />
                  )}
                  {/* Embedded rail: the chart stays pinned above; beneath it the DESK
                      NOTES (the two overall reads, clamped + expandable) — or, when an
                      asset is selected, its docked detail panel. Standalone keeps the
                      overview panel verbatim. */}
                  {embedded ? (
                    selectedAsset ? (
                      <AssetDetailPanel
                        asset={selectedAsset}
                        onClose={handleClosePanel}
                      />
                    ) : (
                      <>
                        <MarketDeskNotes marketPulse={marketPulse} collectionPulse={collectionPulse} />
                        <p className="px-1 font-mono text-[9px] uppercase tracking-[0.08em] text-slate-ds">
                          Select an asset from the tape — its desk read docks here
                        </p>
                      </>
                    )
                  ) : (
                    <AssetDetailPanel
                      asset={selectedAsset}
                      onClose={handleClosePanel}
                    />
                  )}
                </div>
              ) : (
                <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-6 animate-pulse">
                  <div className="h-6 bg-neutral-200 dark:bg-neutral-800 rounded w-1/2 mb-4" />
                  <div className="space-y-3">
                    <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-full" />
                    <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-3/4" />
                    <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-5/6" />
                  </div>
                </div>
              )}
            </div>

            {/* Asset Directory (Master) — the tape */}
            <div className={`lg:col-span-8 ${embedded ? 'lg:order-1' : ''}`}>
              <Suspense fallback={
                <div className="animate-pulse p-4 space-y-2">
                  <div className="h-10 bg-neutral-200 dark:bg-neutral-800 rounded" />
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-14 bg-neutral-200 dark:bg-neutral-800 rounded" />
                  ))}
                </div>
              }>
                <UnifiedAssetDirectory
                  onTabChange={setActiveTab}
                  selectMode={true}
                  selectedAssetId={selectedAsset?.id || null}
                  onAssetSelect={handleAssetSelect}
                  onFirstAssetReady={handleFirstAssetReady}
                  onShareClick={marketPulse ? handleShareClick : undefined}
                  chainId={chainIdValidated}
                  deskReads={deskIntel.deskReads}
                  storyTicks={deskIntel.storyTicks}
                  className="border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden"
                />
              </Suspense>
            </div>
          </div>

          {/* Embedded mobile: DESK NOTES close the page (spec order …table → notes). */}
          {embedded && (
            <div className="lg:hidden mt-4">
              <MarketDeskNotes marketPulse={marketPulse} collectionPulse={collectionPulse} />
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Sheet Drawer - Only rendered on mobile after hydration */}
      {hasMounted && isMobile && (
        <AnimatePresence>
          {selectedAsset && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                onClick={handleClosePanel}
              />

              {/* Bottom Sheet */}
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-hidden"
              >
                {/* Drag Handle */}
                <div
                  className="flex justify-center py-3 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 rounded-t-2xl cursor-grab"
                  onClick={handleClosePanel}
                >
                  <div className="w-10 h-1 bg-neutral-300 dark:bg-neutral-700 rounded-full" />
                </div>

                {/* Scrollable Content */}
                <div className="bg-white dark:bg-neutral-900 overflow-y-auto max-h-[calc(85vh-48px)]">
                  <AssetDetailPanel
                    asset={selectedAsset}
                    onClose={handleClosePanel}
                    className="rounded-none border-0"
                  />
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      )}
    </>
  );

  // Share modals — rendered alongside the body (portals; placement irrelevant).
  const modals = (
    <>
      {/* Token Share Modal */}
      {marketPulse && (
        <TokenShareModal
          isOpen={showTokenShareModal}
          onClose={() => setShowTokenShareModal(false)}
          pulse={marketPulse}
          abstractFearGreed={marketPulse.abstractFearGreed}
          topGainers={top5Gainers}
          topLosers={top5Losers}
          historicalTvl={defiData?.historicalTvl}
          currentTvl={defiData?.tvl}
          tvlChange24h={defiData?.tvlChange24h}
        />
      )}

      {/* NFT Share Modal */}
      <NFTShareModal
        isOpen={showNFTShareModal}
        onClose={() => setShowNFTShareModal(false)}
        collectionPulse={collectionPulse}
        nftHighlights={nftHighlights}
        nftCount={inScopeNftCount}
        totalVolume24h={totalNFTVolume24h}
        abstractFearGreed={marketPulse?.abstractFearGreed}
        historicalTvl={defiData?.historicalTvl}
        currentTvl={defiData?.tvl}
        tvlChange24h={defiData?.tvlChange24h}
      />
    </>
  );

  // Always embedded now: the standalone `/market` page (with its own Navbar/Footer/
  // carousels/ad-banner) was retired in the dashboard cutover. The dashboard shell
  // (`(hub)/[chain]/layout.tsx`) provides all chrome; this just renders body + modals.
  return (
    <div className="flex flex-col">
      {body}
      {modals}
    </div>
  );
}
